import axios, { AxiosError, AxiosResponse } from 'axios';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'INTERNAL_ERROR'
  | 'BAD_REQUEST'
  | 'CONFLICT'
  | 'SERVICE_UNAVAILABLE'
  | 'NETWORK_ERROR'
  | 'UNKNOWN_ERROR';

interface ApiErrorInit {
  error_code: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
  request_id?: string;
  status?: number;
  response?: AxiosResponse;
}

/**
 * Normalized application error thrown by the fetch layer.
 *
 * Extends `Error` so `.message` is the backend's normalized message when
 * present. Carries the structured fields from the backend's error body
 * ({error_code, message, details, request_id}) plus the originating axios
 * `response` for backward compatibility with call sites that still read
 * `err.response?.data` / `err.response?.status`.
 */
export class ApiError extends Error {
  error_code: ErrorCode;
  details?: Record<string, unknown>;
  request_id?: string;
  status?: number;
  response?: AxiosResponse;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.error_code = init.error_code;
    this.details = init.details;
    this.request_id = init.request_id;
    this.status = init.status;
    this.response = init.response;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export function isRecoverableError(status: number | undefined): boolean {
  if (!status) return true;
  // 4xx errors (except 401 which needs redirect) are recoverable
  // 5xx errors are critical and should trigger error boundary
  return status >= 400 && status < 500 && status !== 401;
}

export function isCriticalError(status: number | undefined): boolean {
  if (!status) return false;
  return status >= 500;
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.error_code === 'NETWORK_ERROR';
  }
  if (axios.isAxiosError(error)) {
    return !error.response && error.code === 'ERR_NETWORK';
  }
  return false;
}

export function extractApiError(error: unknown): ApiError {
  // Already normalized
  if (error instanceof ApiError) {
    return error;
  }

  // Handle Axios errors with response
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      error_code?: string;
      message?: string;
      detail?: string;
      details?: Record<string, unknown>;
      request_id?: string;
    }>;

    // Network error (no response) - this is NOT a CORS error
    // When the browser can't reach the server at all, it reports ERR_NETWORK
    // The browser console may show "CORS error" but it's actually a network error
    if (!axiosError.response) {
      const url = axiosError.config?.url || 'backend';
      return new ApiError({
        error_code: 'NETWORK_ERROR',
        message: `Cannot connect to ${url}. The server may be down or unreachable.`,
        status: undefined,
      });
    }

    const data = axiosError.response.data;
    const status = axiosError.response.status;

    // Extract request_id from response body, falling back to the
    // X-Request-Id response header.
    const requestId =
      data?.request_id ||
      (axiosError.response.headers?.['x-request-id'] as string | undefined) ||
      (axiosError.response.headers?.['X-Request-Id'] as string | undefined);

    // Handle normalized API error format
    if (data?.error_code) {
      return new ApiError({
        error_code: data.error_code as ErrorCode,
        message: data.message || 'An error occurred',
        details: data.details,
        request_id: requestId,
        status,
        response: axiosError.response,
      });
    }

    // Handle legacy FastAPI format with "detail"
    if (data?.detail) {
      return new ApiError({
        error_code: statusToErrorCode(status),
        message: typeof data.detail === 'string' ? data.detail : 'An error occurred',
        request_id: requestId,
        status,
        response: axiosError.response,
      });
    }

    // Fallback for unknown response format
    return new ApiError({
      error_code: statusToErrorCode(status),
      message: 'An error occurred',
      request_id: requestId,
      status,
      response: axiosError.response,
    });
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    return new ApiError({
      error_code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
    });
  }

  // Handle unknown error types
  return new ApiError({
    error_code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  });
}

/**
 * Toast a normalized API error. Uses `extractApiError(err).message` as the
 * toast title (falling back to `fallback`), and surfaces the request_id as
 * the toast description ("Ref: <id>") when present.
 */
export function toastApiError(err: unknown, fallback: string): void {
  const apiError = extractApiError(err);
  const message = apiErrorMessage(err, fallback);
  toast.error(
    message,
    apiError.request_id ? { description: `Ref: ${apiError.request_id}` } : undefined,
  );
}

/** User-facing text for an API error: the translated refusal reason when the
 *  backend sent a known `details.reason`, else its message, else `fallback`. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const apiError = extractApiError(err);
  const key = apiErrorReasonKey(err);
  return key ? i18n.t(key, reasonValues(apiError.details)) : apiError.message || fallback;
}

function statusToErrorCode(status: number): ErrorCode {
  const mapping: Record<number, ErrorCode> = {
    400: 'BAD_REQUEST',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    409: 'CONFLICT',
    422: 'VALIDATION_ERROR',
    503: 'SERVICE_UNAVAILABLE',
  };
  return mapping[status] || (status >= 500 ? 'INTERNAL_ERROR' : 'UNKNOWN_ERROR');
}

export function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.status;
  }
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * Classifies an error caught by an error boundary for display purposes.
 *
 * Only a real backend 5xx (the only case queryClient.ts throws to an error
 * boundary for) is a "server error". Any other error caught by a boundary is
 * a client-side crash (React render error, JS exception) unrelated to the
 * backend, so it must not be mislabeled as one.
 */
export function getErrorPresentation(error: unknown): { isServerError: boolean; requestId?: string } {
  return {
    isServerError: isCriticalError(getErrorStatus(error)),
    requestId: extractApiError(error).request_id,
  };
}

/** Curried `onError` for React Query mutations: names the failure, then hands
 *  the error to `toastApiError` so the backend's own message wins when it has
 *  one. Lives here rather than in a hooks file because every mutation in the
 *  app needs the same three lines. */
export const apiErrorToast =
  (key: string, fallback: string) =>
  (error: unknown): void =>
    toastApiError(error, i18n.t(key, fallback))

export const INVALID_CREDENTIALS_REASON = 'invalid_credentials';

const LOGIN_ERROR_KEYS: Record<string, string> = {
  [INVALID_CREDENTIALS_REASON]: 'login.errors.invalidCredentials',
  account_not_activated: 'login.errors.accountNotActivated',
  account_disabled: 'login.errors.accountDisabled',
  email_not_verified: 'login.errors.emailNotVerified',
  sso_only: 'login.errors.ssoOnly',
  password_login_disabled: 'login.errors.passwordLoginDisabled',
  no_account: 'login.errors.noAccount',
  already_registered: 'login.errors.alreadyRegistered',
  signup_closed: 'login.errors.signupClosed',
  oauth_failed: 'login.errors.oauthFailed',
};

const REASON_KEYS: Record<string, string> = {
  ...LOGIN_ERROR_KEYS,
  user_has_gaia_evidence: 'users.errors.hasGaiaEvidence',
  user_already_member_with_role: 'users.errors.alreadyMemberWithRole',
  file_too_large: 'fileDropzone.validation.fileTooLarge',
  unsupported_file_type: 'fileDropzone.validation.unsupportedFileType',
};

/** Interpolation values of a refusal's details: lists joined into readable text. */
function reasonValues(details: Record<string, unknown> | undefined): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(details ?? {}).map(([name, value]) => [name, Array.isArray(value) ? value.join(', ') : value]),
  );
}

/** Reason code of a refused request: `details.reason` of the normalized error, or null. */
export function loginErrorReason(error: unknown): string | null {
  const reason = extractApiError(error).details?.reason;
  return typeof reason === 'string' ? reason : null;
}

/** i18n key for a refused login's reason code; null when the reason is not a known login refusal. */
export function loginErrorKey(error: unknown): string | null {
  const reason = loginErrorReason(error);
  return reason ? LOGIN_ERROR_KEYS[reason] ?? null : null;
}

/** i18n key for any API error carrying a known `details.reason`; null otherwise. */
export function apiErrorReasonKey(error: unknown): string | null {
  const reason = loginErrorReason(error);
  return reason ? REASON_KEYS[reason] ?? null : null;
}

/** Translated message for a login refusal code (e.g. a login URL's `?error=`); null when unknown. */
export function loginReasonMessage(reason: string | null): string | null {
  const key = reason ? LOGIN_ERROR_KEYS[reason] : undefined;
  return key ? i18n.t(key) : null;
}

/** Message to show for a failed login: the translated reason, else the backend's own message. */
export function loginErrorMessage(error: unknown): string {
  const key = loginErrorKey(error);
  return key ? i18n.t(key) : extractApiError(error).message;
}
